import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useInvitationStore } from '@/lib/store/invitationStore';
import { Alert, Button, Card } from '@/components/common';
import { formatDistanceToNow } from 'date-fns';

export default function InvitationsList() {
  const { teamId } = useParams<{ teamId: string }>();
  const { teamInvitations, isLoading, error, fetchTeamInvitations, resendInvitation, cancelInvitation } = useInvitationStore();
  
  useEffect(() => {
    if (teamId) {
      fetchTeamInvitations(teamId);
    }
  }, [teamId, fetchTeamInvitations]);
  
  const handleResend = async (invitationId: string) => {
    try {
      await resendInvitation(invitationId);
    } catch (error) {
      console.error('Failed to resend invitation:', error);
    }
  };
  
  const handleCancel = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="error" className="mb-4">
        {error}
      </Alert>
    );
  }
  
  if (teamInvitations.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-6 text-gray-500">
          No pending invitations.
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Pending Invitations</h2>
      
      <div className="space-y-4">
        {teamInvitations.map(invitation => (
          <div 
            key={invitation.id} 
            className="p-4 border rounded-lg flex flex-col sm:flex-row sm:items-center justify-between"
          >
            <div>
              <div className="font-medium">{invitation.email}</div>
              <div className="text-sm text-gray-500">
                <span className="capitalize">{invitation.role}</span> • 
                <span className="capitalize ml-1">
                  {invitation.shift_type === 'days_only' ? 'Days Only' : 
                   invitation.shift_type === 'nights_only' ? 'Nights Only' : 
                   'Both Day and Night Shifts'}
                </span> • 
                <span className="ml-1">
                  Sent {formatDistanceToNow(new Date(invitation.created_at), { addSuffix: true })}
                </span>
              </div>
            </div>
            
            <div className="flex space-x-2 mt-2 sm:mt-0">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleResend(invitation.id)}
              >
                Resend
              </Button>
              <Button 
                variant="danger" 
                size="sm"
                onClick={() => handleCancel(invitation.id)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
} 